import {
  classifyError,
  newErrorRef,
  refFromMessage,
  sanitizeErrorMessage,
  withRefText,
  type ErrorCategory,
} from "@/lib/errorInsight";

// Klientens side av feilinnsikten (PRD 1.0 US22): feil fra dataprovideren
// får en referanse i meldingen brukeren ser, og logges med log_client_error.
// Logging feiler aldri høyt; det skal ikke lage nye feil.

export type Rpc = (
  fn: string,
  args: Record<string, unknown>,
) => PromiseLike<{ error: unknown }>;

type Report = {
  category: ErrorCategory;
  source: string;
  message: string;
  context?: Record<string, string | number | undefined>;
};

// Samme feil om og om igjen (f.eks. en liste som hentes på nytt) logges
// bare én gang per halvminutt, og høyst 20 ganger i minuttet per fane.
const DEDUPE_MS = 30_000;
const MAX_PER_MINUTE = 20;

export const createErrorReporter = (getRpc: () => Rpc) => {
  const recent = new Map<string, number>();
  let window: number[] = [];

  const report = ({ category, source, message, context = {} }: Report) => {
    const ref = newErrorRef();
    const clean = sanitizeErrorMessage(message);
    const now = Date.now();
    const key = `${category}|${source}|${clean}`;
    window = window.filter((t) => now - t < 60_000);
    if ((recent.get(key) ?? 0) > now - DEDUPE_MS) return ref;
    if (window.length >= MAX_PER_MINUTE) return ref;
    recent.set(key, now);
    window.push(now);
    try {
      void Promise.resolve(
        getRpc()("log_client_error", {
          p_ref: ref,
          p_category: category,
          p_source: source,
          p_message: clean,
          p_context: {
            ...context,
            route:
              typeof location === "undefined"
                ? undefined
                : location.hash.replace(/\/\d+(?=\/|$)/g, "/:id").slice(0, 100),
          },
        }),
      ).catch(() => undefined);
    } catch {
      // Ingen klient (f.eks. utlogget): referansen vises uansett.
    }
    return ref;
  };

  return { report };
};

const isAbort = (error: unknown) =>
  (error as { name?: string })?.name === "AbortError";

/**
 * Pakker inn alle metoder på dataprovideren: en feil får referansen i
 * meldingen og logges. Har edge-funksjonen allerede gitt en referanse,
 * brukes den, og feilen er allerede logget der.
 */
export const withErrorInsight = <T extends object>(
  provider: T,
  reporter: ReturnType<typeof createErrorReporter>,
): T =>
  new Proxy(provider, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== "function" || typeof prop !== "string") return value;
      return (...args: unknown[]) => {
        const handle = (error: unknown) => {
          if (isAbort(error) || error == null) throw error;
          const e = error as {
            message?: string;
            ref?: string;
            status?: number;
          };
          const message =
            typeof error === "string" ? error : (e.message ?? String(error));
          const ref =
            e.ref ??
            refFromMessage(message) ??
            reporter.report({
              category: classifyError(error),
              source: "klient",
              message,
              context: {
                method: prop,
                resource: typeof args[0] === "string" ? args[0] : undefined,
                status: e.status,
              },
            });
          if (typeof error === "string") {
            throw Object.assign(new Error(withRefText(error, ref)), { ref });
          }
          try {
            e.message = withRefText(message, ref);
            e.ref = ref;
          } catch {
            // Frosne feilobjekter: kast som de er.
          }
          throw error;
        };
        try {
          const result = value.apply(target, args);
          return result instanceof Promise ? result.catch(handle) : result;
        } catch (error) {
          return handle(error);
        }
      };
    },
  });
